import React, { useMemo } from 'react'
import { UserService } from '@duck/api-definitions'
import { FunnelIcon } from '@heroicons/react/24/outline'
import {
  getRoleBadgeColor,
  formatRole,
  formatAccessGroups,
  roleFilterOptions,
  type Column,
} from './utils'
import { UsersTableSkeleton } from './UsersTableSkeleton'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { useTar } from '@/modules'
import type { TranslationKey } from '@/modules/i18n/types'
import { useLoadMoreOnScroll } from '@/hooks/useLoadMoreOnScroll'
import { renderSortIcon } from '@/hooks/TableSettings/generalTable.utils'
import type { EnumFilterOption } from '@/hooks/TableSettings/generalTable.utils'
import {
  TextFilterPopover,
  EnumFilterDropdown,
  ColumnVisibilityDropdown,
} from '@/hooks/TableSettings/tableSettings.components'
import { FilterSetManager } from '@/hooks/TableSettings/FilterSetManager'
import type { SortDirection, TableSortState } from '@/hooks/TableSettings/useTableSettings'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { OrganizationDataInput } from '@/components/DataInputs/OrganizationDataInput'
import { useOrganizations } from '@/storeHooks/useOrganizations'
import { UserAvatar } from '../UserAvatar'
import { CopyableId, CopyableText } from '@duck/ui'

interface UsersTableFilterApi {
  getColumnFilter: (columnKey: string) => string
  setColumnFilter: (columnKey: string, value: string) => void
  getColumnFilterArray: (columnKey: string) => string[]
  setColumnFilterArray: (columnKey: string, value: string[]) => void
  toggleColumnFilterArrayItem: (columnKey: string, item: string) => void
  clearColumnFilter: (columnKey: string) => void
}

interface UsersTableProps {
  users: UserService.User[]
  totalUsers: number
  visibleColumns: Column[]
  sortField: keyof UserService.User
  sortDirection: 'asc' | 'desc'
  onSort: (field: keyof UserService.User) => void
  onUserClick?: (user: UserService.User) => void
  loadingMode?: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  isPageLoading?: boolean
  filterApi?: UsersTableFilterApi
  /** Available access group values for the access groups enum filter. */
  accessGroupOptions?: string[]
  hasNoFilterResults?: boolean
  onClearFilters?: () => void
  /** When provided, Clear all + Column visibility are shown in the table header */
  tableSettings?: {
    hasActiveSettings: () => boolean
    clearAllSettings: () => void
    isColumnVisible: (key: string) => boolean
    toggleColumnVisibility: (key: string) => void
    availableColumns: Array<{ key: string; label: string }>
    /** Filter set manager props */
    tableId: string
    sortColumn: string | null
    sortDirection: SortDirection
    filters: Record<string, string | string[]>
    visibleColumns: Record<string, boolean>
    defaultVisibleColumns: Record<string, boolean>
    filterLabels: Record<string, Record<string, string>>
    setSort: (columnKey: string | null, direction: SortDirection) => void
    clearColumnFilter: (columnKey: string) => void
    applyFilterSet: (sort: TableSortState, filters: Record<string, string | string[]>, visibleColumns?: Record<string, boolean>, filterLabels?: Record<string, Record<string, string>>) => void
  }
}

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  totalUsers,
  visibleColumns,
  sortField,
  sortDirection,
  onSort,
  onUserClick,
  loadingMode = false,
  hasMore = false,
  onLoadMore,
  isPageLoading = false,
  filterApi,
  accessGroupOptions,
  hasNoFilterResults = false,
  onClearFilters,
  tableSettings,
}) => {
  const { t } = useTar()
  const { idToNameMap } = useOrganizations()

  const getEnumOptionsForColumn = useMemo((): ((filterKey: string) => EnumFilterOption[] | undefined) => {
    return (filterKey: string) => {
      if (filterKey === 'role') {
        return roleFilterOptions.map((o) => ({
          value: o.value,
          label: o.label,
          component: (
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(o.value)}`}
            >
              {t(o.label)}
            </span>
          ),
        }))
      }
      if (filterKey === 'accessGroups' && accessGroupOptions) {
        return accessGroupOptions.map((group) => ({
          value: group,
          label: group as TranslationKey,
          component: (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-muted/10 text-muted border border-muted/20">
              {group}
            </span>
          ),
        }))
      }
      return undefined
    }
  }, [t, accessGroupOptions])

  const { sentinelRef: loadMoreSentinelRef, sentinelIndex: loadMoreSentinelIndex } = useLoadMoreOnScroll<HTMLTableRowElement>({
    hasMore,
    onLoadMore,
    isLoading: isPageLoading,
    disabled: loadingMode,
    itemCount: users.length,
    offsetFromEnd: 10,
  })

  const cellRenderers: Partial<Record<keyof UserService.User, (user: UserService.User) => React.ReactNode>> = {
    id: (user) => (
      <span className="text-xs font-mono text-muted-foreground">
        {user.id}
      </span>
    ),
    email: (user) => (
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <UserAvatar size="md" user={user} />
        </div>
        <div className="ms-4">
          <p className="text-sm font-medium text-foreground">
            {user.firstName} {user.lastName}
          </p>
          <CopyableText value={user.email} className="text-sm text-muted-foreground ps-0">
            {user.email}
          </CopyableText>
        </div>
      </div>
    ),
    role: (user) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
        {formatRole(user.role)}
      </span>
    ),
    accessGroups: (user) => (
      <span className="text-sm text-muted-foreground">
        {formatAccessGroups(user.accessGroups)}
      </span>
    ),
    organizationId: (user) => (
      <span className="text-sm text-muted-foreground">
        {user.organizationId
          ? (idToNameMap.get(user.organizationId) ?? user.organizationId)
          : t('common.na')}
      </span>
    ),
    invitationId: (user) => (
      user.invitationId ? <CopyableId value={user.invitationId} /> : '-'
    ),
    createdAt: (user) => (
      <RelativeDateTime
        isoDateString={user.createdAt}
        className="text-sm text-muted-foreground"
      />
    ),
    lastLoginAt: (user) => (
      <RelativeDateTime
        isoDateString={user.lastLoginAt || ''}
        className="text-sm text-muted-foreground"
        fallbackText={t('common.never')}
      />
    ),
    avatar: (user) => (
      <span className="text-sm text-muted-foreground">
        {user.avatar ? t('common.hasAvatar') : t('common.noAvatar')}
      </span>
    ),
    notes: (user) => (
      <span className="text-sm text-muted-foreground max-w-xs truncate block" title={user.notes || ''}>
        {user.notes || t('common.none')}
      </span>
    ),
    personId: (user) => (
      <span className="text-sm text-muted-foreground">
        {user.personId || t('common.na')}
      </span>
    ),
  }

  const renderCellContent = (user: UserService.User, columnKey: keyof UserService.User) => {
    const renderer = cellRenderers[columnKey]
    return renderer ? renderer(user) : null
  }

  return (
    <div className="bg-card shadow overflow-hidden rounded-md sm:rounded-md border border-border">
      <div className="px-4 py-5 sm:px-6 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-lg leading-6 font-medium text-foreground">
            {t('users.allUsers')}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {loadingMode ? t('common.loading') : t('common.XRecords', { count: users.length })}
          </p>
        </div>
        {tableSettings && (
          <div className="flex items-center gap-2">
            <FilterSetManager
              tableId={tableSettings.tableId}
              sortColumn={tableSettings.sortColumn}
              sortDirection={tableSettings.sortDirection}
              filters={tableSettings.filters}
              visibleColumns={tableSettings.visibleColumns}
              defaultVisibleColumns={tableSettings.defaultVisibleColumns}
              filterLabels={tableSettings.filterLabels}
              setSort={tableSettings.setSort}
              clearColumnFilter={tableSettings.clearColumnFilter}
              clearAllSettings={tableSettings.clearAllSettings}
              applyFilterSet={tableSettings.applyFilterSet}
              columns={tableSettings.availableColumns}
            />
            <ColumnVisibilityDropdown
              availableColumns={tableSettings.availableColumns}
              isColumnVisible={tableSettings.isColumnVisible}
              toggleColumnVisibility={tableSettings.toggleColumnVisibility}
            />
          </div>
        )}
      </div>

      <div className="overflow-x-auto overflow-y-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-secondary/5">
            <tr>
              {visibleColumns.map((column) => {
                const filterKey = column.filterKey ?? column.key
                const isEnumFilter = column.filterType === 'enum'
                const isTextFilter = column.filterType === 'text'
                const isAsyncSearchFilter = column.filterType === 'async-search'
                const filterValue = filterApi && isTextFilter ? filterApi.getColumnFilter(filterKey) : ''
                const filterArray =
                  filterApi && (isEnumFilter || isAsyncSearchFilter)
                    ? filterApi.getColumnFilterArray(filterKey)
                    : []
                const hasFilter = isTextFilter ? !!filterValue?.trim() : (filterArray?.length ?? 0) > 0
                const enumOptions = isEnumFilter ? getEnumOptionsForColumn(filterKey) : undefined

                return (
                  <th
                    key={column.key}
                    className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider"
                  >
                    <div className="flex items-center gap-2">
                      {column.sortable ? (
                        <button
                          onClick={() => onSort(column.key)}
                          className="group flex items-center gap-1.5 hover:text-foreground transition-colors"
                        >
                          <span>{t(column.label)}</span>
                          {renderSortIcon(
                            sortField === column.key ? sortDirection : null
                          )}
                        </button>
                      ) : (
                        <span>{t(column.label)}</span>
                      )}
                      {isEnumFilter && enumOptions && filterApi && (
                        <EnumFilterDropdown
                          label={column.label}
                          labelT={t}
                          enumOptions={enumOptions}
                          filterArray={filterArray}
                          hasFilter={hasFilter}
                          onToggleFilterItem={(value) =>
                            filterApi.toggleColumnFilterArrayItem(
                              filterKey,
                              value
                            )
                          }
                          onClearFilter={() =>
                            filterApi.clearColumnFilter(filterKey)
                          }
                        />
                      )}
                      {isTextFilter && filterApi && (
                        <TextFilterPopover
                          label={column.label}
                          labelT={t}
                          filterValue={filterValue}
                          hasFilter={hasFilter}
                          onFilterChange={(value) =>
                            filterApi.setColumnFilter(filterKey, value)
                          }
                          onClearFilter={() =>
                            filterApi.clearColumnFilter(filterKey)
                          }
                        />
                      )}
                      {isAsyncSearchFilter &&
                        filterApi &&
                        filterKey === 'organizationId' && (
                          <OrganizationDataInput
                            selected={filterArray}
                            onChange={(values) =>
                              filterApi.setColumnFilterArray(filterKey, values)
                            }
                            labels={{
                              placeholder: t('common.search'),
                              searchPlaceholder: t('common.search'),
                            }}
                            listClassName="w-[360px]"
                            customTrigger={({ selected }) => (
                              <button
                                className={`flex items-center justify-center h-4 w-4 hover:text-foreground transition-colors ${selected.length > 0
                                  ? 'text-accent'
                                  : 'text-muted-foreground/50'
                                  }`}
                              >
                                <FunnelIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                          />
                        )}
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {loadingMode ? (
              <UsersTableSkeleton visibleColumns={visibleColumns} rowCount={8} />
            ) : hasNoFilterResults ? (
              <tr>
                <td colSpan={visibleColumns.length} className="px-6 py-8">
                  <NoDataEmptyState
                    icon={FunnelIcon}
                    title={t('common.noFilterResults')}
                    description={t('common.noFilterResultsDescription')}
                    buttonText={onClearFilters ? t('common.resetFilters') : undefined}
                    onButtonClick={onClearFilters}
                  />
                </td>
              </tr>
            ) : (
              users.map((user, index) => {
                const isLoadMoreSentinel = hasMore && !loadingMode && index === loadMoreSentinelIndex

                return (
                  <tr
                    key={user.id}
                    ref={isLoadMoreSentinel ? loadMoreSentinelRef : undefined}
                    className={`hover:bg-secondary/5 ${onUserClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onUserClick?.(user)}
                  >
                    {visibleColumns.map((column) => (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                        {renderCellContent(user, column.key)}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {hasMore && !loadingMode && (
        <div className="flex items-center justify-center py-4">
          {isPageLoading && (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent"></div>
              <span className="ms-2 text-sm text-muted-foreground">{t('common.loading')}</span>
            </div>
          )}
        </div>
      )}

      {!loadingMode && !hasNoFilterResults && users.length === 0 && (
        <div className="text-center py-12">
          <p className="text-sm text-muted-foreground">{t('users.noUsers')}</p>
        </div>
      )}
    </div>
  )
}
